import { useEffect } from 'react';

/**
 * SEO Component - динамическое управление мета-тегами для каждой страницы
 * 
 * @param {Object} props
 * @param {string} props.title - Заголовок страницы (обязательно)
 * @param {string} props.description - Описание страницы
 * @param {string} props.keywords - Ключевые слова
 * @param {string} props.canonical - Канонический URL
 * @param {string} props.image - URL изображения для соцсетей
 * @param {string} props.type - Тип страницы (website, article, profile)
 * @param {string} props.locale - Локаль (ru_RU, en_US)
 * @param {Object} props.structuredData - JSON-LD структурированные данные
 */
const SEO = ({
  title,
  description = 'Spiria — современная платформа для управления здоровьем',
  keywords = '',
  canonical = '',
  image = '%PUBLIC_URL%/og-image.png',
  type = 'website',
  locale = 'ru_RU',
  structuredData = null,
  noindex = false,
  nofollow = false,
  children
}) => {
  useEffect(() => {
    // Устанавливаем заголовок
    const siteName = 'Spiria';
    const fullTitle = title ? `${title} — ${siteName}` : siteName;
    document.title = fullTitle;

    // Утилита для обновления/создания meta тегов
    const setMetaTag = (name, content, property = false) => {
      if (!content) return;
      
      const selector = property 
        ? `meta[property="${name}"]` 
        : `meta[name="${name}"]`;
      
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Основные мета-теги
    setMetaTag('description', description);
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Robots
    const robotsContent = [
      noindex ? 'noindex' : 'index',
      nofollow ? 'nofollow' : 'follow',
      'max-image-preview:large',
      'max-snippet:-1',
      'max-video-preview:-1'
    ].join(', ');
    setMetaTag('robots', robotsContent);

    // Canonical URL
    if (canonical) {
      let linkCanonical = document.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonical);
    }

    // Open Graph
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:image', image, true);
    setMetaTag('og:locale', locale, true);
    if (canonical) {
      setMetaTag('og:url', canonical, true);
    }

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', image);

    // JSON-LD структурированные данные
    if (structuredData) {
      let scriptLd = document.querySelector('script[type="application/ld+json"]#dynamic-ld');
      if (!scriptLd) {
        scriptLd = document.createElement('script');
        scriptLd.setAttribute('type', 'application/ld+json');
        scriptLd.id = 'dynamic-ld';
        document.head.appendChild(scriptLd);
      }
      scriptLd.textContent = JSON.stringify(structuredData);
    }

    // Cleanup при размонтировании
    return () => {
      // Удаляем динамический JSON-LD
      const dynamicLd = document.querySelector('script[type="application/ld+json"]#dynamic-ld');
      if (dynamicLd) {
        dynamicLd.remove();
      }
    };
  }, [title, description, keywords, canonical, image, type, locale, structuredData, noindex, nofollow]);

  return children || null;
};

export default SEO;
